import { alpha, createTheme } from "@mui/material/styles";
import { gridClasses } from "@mui/x-data-grid-premium";
import themeTokens from "../../constants/scrap_chef_constants/theme_tokens.json";
import "@fontsource/source-sans-pro/400.css";
import "@fontsource/source-sans-pro/600.css";
import "@fontsource/source-sans-pro/700.css";
import "@fontsource/source-code-pro/400.css";
import "@fontsource/source-code-pro/700.css";

let theme = createTheme({
  typography: {
    ...themeTokens.typography,
    button: {
      ...themeTokens.typography.button,
      textTransform: themeTokens.typography.button
        .textTransform as React.CSSProperties["textTransform"],
    },
  },
  palette: themeTokens.palette,
});

theme = createTheme(theme, {
  components: {
    MuiTypography: {
      defaultProps: {
        variantMapping: {},
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
        },
        sizeLarge: {
          fontSize: 16,
          fontWeight: 700,
          lineHeight: 1.5,
        },
        sizeSmall: {
          fontSize: 12,
          fontWeight: 700,
          lineHeight: 1.6,
        },
      },
      defaultProps: {
        disableElevation: true,
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          color: theme.palette.text.primary,
          borderColor: alpha(theme.palette.common.black, 0.23),
          fontWeight: theme.typography.fontWeightMedium,
          "&.Mui-selected": {
            color: theme.palette.primary.main,
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            "&:hover": {
              backgroundColor: alpha(theme.palette.primary.main, 0.12),
            },
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: theme.spacing(0.5),
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          paddingTop: theme.spacing(4),
        },
      },
      defaultProps: {
        variant: "h4",
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          marginTop: theme.spacing(1),
          paddingBottom: theme.spacing(0),
          marginBottom: theme.spacing(2),
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          paddingLeft: theme.spacing(3),
          paddingRight: theme.spacing(3),
          paddingBottom: theme.spacing(3),
        },
      },
    },
    MuiAccordion: {
      defaultProps: {
        elevation: 0,
        square: true,
        disableGutters: true,
      },
      styleOverrides: {
        root: {
          "&:before": {
            display: "none",
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: theme.palette.grey[900],
          color: theme.palette.common.white,
          fontSize: 12,
          fontWeight: 400,
          lineHeight: 1.6,
          fontFamily: "Source Code Pro, monospace",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        colorDefault: {
          color: theme.palette.text.primary,
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        colCell: {
          height: "32px",
        },
        root: {
          "& .MuiDataGrid-main": {
            "& > div:first-of-type": {
              backgroundColor: theme.palette.grey[50],
            },
          },
          "& .MuiDataGrid-cell": {
            borderRight: `1px solid ${theme.palette.grey[100]}`,
            "&:last-child": {
              borderRight: "none",
            },
            ...theme.typography.body1,
          },
          "& .MuiDataGrid-columnHeader": {
            backgroundColor: theme.palette.grey[50],
            borderRight: `1px solid ${theme.palette.grey[100]}`,
            "&:last-child": {
              borderRight: "none",
            },
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            ...theme.typography.body2,
            color: theme.palette.text.secondary,
          },
        },
      },
      defaultProps: {
        disableRowSelectionOnClick: true,
        disableColumnMenu: true,
        hideFooter: true,
        columnHeaderHeight: 28,
        rowHeight: 32,
        sx: {
          [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]:
            {
              outline: "none",
            },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: theme.spacing(0.5),
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: "none",
        },
      },
    },
    MuiMenuItem: {
      defaultProps: {
        sx: {
          px: 1,
        },
      },
    },
  },
});

export default theme;

declare module "@mui/material/styles" {
  interface TypographyVariants {
    body1Mono: React.CSSProperties;
    body2Mono: React.CSSProperties;
    fontWeightSemiBold: number;
  }

  interface TypographyVariantsOptions {
    body1Mono?: React.CSSProperties;
    body2Mono?: React.CSSProperties;
    fontWeightSemiBold?: number;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    body1Mono: true;
    body2Mono: true;
  }
}

declare module "@mui/material/styles" {
  interface Palette {
    data: {
      blue: string;
      pink: string;
      lightblue: string;
      orange: string;
      green: string;
      red: string;
      teal: string;
      violet: string;
      rose: string;
    };
    categorical: {
      cool: string[];
      warm: string[];
    };
  }

  interface PaletteOptions {
    data?: {
      blue: string;
      pink: string;
      lightblue: string;
      orange: string;
      green: string;
      red: string;
      teal: string;
      violet: string;
      rose: string;
    };
    categorical?: {
      cool: string[];
      warm: string[];
    };
  }
}
